import React from "react";
import * as d3 from "d3";
import { renderToString } from "react-dom/server";
import { get as _get, isEqual, lowerCase } from "lodash";

export default class ScriptDisplay extends React.Component {
  static defaultProps = {
    onElementMove: () => {},
    onLinkElementStart: () => {},
    onLinkElementComplete: () => {},
  };

  constructor(props) {
    super(props);
    this.IS_EDITABLE = props.is_editable;

    this.state = {
      data: [],
      valid: true,
      linkSource: [],
    };

    // BASIC DISP VARS

    this.DISPLAY_SETTINGS = {
      viewBox: {
        x: 2000,
        y: 10000,
        defaultPadding: {
          x: 350,
          y: 100,
        },
      },
      connectorLabel: {
        width: 80,
        height: 30,
        padding: {
          x: 8,
          y: 2,
        },
        maxTextLength: 10,
      },
      connectorLine: {
        maxArcRadius: 8,
        minWidth: 2,
        maxWidth: 40,
      },
      box: {
        minWidth: 240,
        height: 90,
        padding: {
          x: 10,
          y: 280,
        },
        icon: {
          width: 30,
          height: 30,
          dx: -10,
          dy: -15,
        },
      },
    };

    this.startBoxSavedOffset = {
      x: 0,
      y: 0,
    };

    this.connectionMode = false;
    // we use this to track the status of the drag and make sure that we have everything that we need. ht
    this.dragArray = [null, null];
  }

  componentDidMount() {
    this.buildSVGWrapper();
    this.appendDefs();
    if (this.props.data) {
      this.prepareToBuildScript();
      this.buildScript();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      !isEqual(prevProps.data, this.props.data) ||
      !isEqual(prevProps.editingElement, this.props.editingElement)
    ) {
      // d3.select('svg').remove();f
      this.prepareToBuildScript();
      this.buildScript();
    }

    if (!isEqual(prevState, this.state)) {
      this.buildScript();
    }
  }

  prepareToBuildScript = () => {
    this.setState({ data: this.organizeScriptElements() });
    this.connectorWidthScale = this.getConnectorWidthScale();
  };

  //this is off because the "pass through instance is added later, we need to fix that.
  getBoxWidth = (elem) => {
    let { DISPLAY_SETTINGS: disp } = this;
    let calc_length =
      disp.box.padding.x * 2 +
      (disp.connectorLabel.width + disp.connectorLabel.padding.x * 2) *
        elem.connectors.length;
    return calc_length < disp.box.minWidth ? disp.box.minWidth : calc_length;
  };

  getElemSelector = (element_hash) => {
    return "g-" + element_hash;
  };

  displayOrTruncateTextWithEllipsis = (text, maxChars) => {
    if (!text || typeof text !== "string") return;

    if (text.length > maxChars) {
      return text.slice(0, maxChars) + "...";
    } else {
      return text;
    }
  };

  getConnectorWidthScale = () => {
    this.maxResponseCount = Math.max(
      ...this.state.data.map((d) => d.response_count)
    );
    return d3
      .scaleLinear()
      .domain([0, this.maxResponseCount || 1])
      .range([
        this.DISPLAY_SETTINGS.connectorLine.minWidth,
        this.DISPLAY_SETTINGS.connectorLine.maxWidth,
      ]);
  };

  //sort elems by heirarchy and script order
  //this generally sorts the elems into "levels" we use for a vertical display, which are calculated based on what connects to what
  organizeScriptElements = () => {
    let data = [...this.props.data];

    // start by locating the start
    let start = data.find((el) => el.elementtype === "start");

    if (!start) return []; // if no start element, then there's nothing to render.

    //get in min left and top values to start
    const min_left = Math.min(...data.map((d) => d.pos_left));
    const min_top = Math.min(...data.map((d) => d.pos_top));
    this.startBoxSavedOffset = {
      x: min_left,
      y: min_top,
    };

    start.level = 1;

    let validScript = true;

    let organized = [start];
    //define process to add connectors
    const addConnectors = (elem) => {
      if (!elem.level) elem.level = [...organized].pop().level + 1;
      //lowercase this for comparison.
      elem.elementtype = lowerCase(elem.elementtype);
      elem.connectors.forEach((con) => {
        //is connecting elem already organized? Make sure the level is at least one higher than elem
        let existingIndex = organized.findIndex(
          (el) => el.element_hash === con.next_el_hash
        );
        if (existingIndex > -1) {
          if (organized[existingIndex].level <= elem.level) {
            organized[existingIndex].level = elem.level + 1;
          }
        }
        //not already organized, to add it now;
        else {
          let connectedElem = data.find(
            (el) => el.element_hash === con.next_el_hash
          );
          if (!connectedElem) {
            // validScript = false;
            console.warn("Script Invalid!");
            // return;
            connectedElem = {};
          }
          connectedElem.level = elem.level + 1;
          organized.push(connectedElem);
        }

        //append the the key string in the data so we can build connections between the blocks
        con.key = this.getConnectorKey({ ...con, parentData: elem });
      });
      //last element will have no connectors. append one so we can link if a new item is created
      // has to be down here so we don't throw an error.
      if (!elem.connectors.length)
        elem.connectors.push({
          ordinal_position: 1,
          key: this.getConnectorKey({ parentData: elem }),
        });
    };

    if (!validScript) return [];
    data.forEach((d) => addConnectors({ ...d }));
    return data;
  };

  //thank god for SO
  getElementTransformation = (domElem) => {
    // consolidate the SVGTransformList containing all transformations
    // to a single SVGTransform of type SVG_TRANSFORM_MATRIX and get
    // its SVGMatrix.

    try {
      var matrix = domElem.transform.baseVal.consolidate().matrix;
      // As ƒper definition values e and f are the ones for the translation.
      return [matrix.e, matrix.f];
    } catch {
      return [0, 0];
    }
  };

  getConnectorLine = (conn, idx, connArr) => {
    //where 0,0 is the upper right of the connectorLabel box g element
    /*
     * Technique: find the translation of the parent element, and then how far bottom center of the
     * translation element is coded of that.
     * Then find the translation of the target element, and how far the middle top connectorLabel box is off of that.
     *
     *
     * From these two points, we can extrapolate how far we need to go.
     * */

    // start by getting the parent.
    let { DISPLAY_SETTINGS: disp } = this;

    let parent = document.getElementById(
      this.getElemSelector(conn.parentData.element_hash)
    );
    let destination = document.getElementById(
      this.getElemSelector(conn.next_el_hash)
    );

    if (!destination) return null;

    let sourceOffset = this.getElementTransformation(parent);
    let destinationOffset = this.getElementTransformation(destination);

    //the destination connectorLabel is in the middle top of the box
    let destinationWidth = +(
      destination.getAttribute("box-width") || disp.box.minWidth
    );

    // need to calculate the sum of the widths of the connectors lines that came before it so that we can displace
    // by that much. Otherwise, the lines overlap and you can't use width as a visual gauge of total responses

    //determing if we are moving left or right -- that is, if we need to add or subtract the connection offset
    let goingRight = sourceOffset[0] < destinationOffset[0];
    let connOffset;

    if (goingRight) {
      connOffset = connArr.reduce(
        (r, d, i, a) => {
          if (i >= idx) return r;
          return (r -= d.__data__.stroke_width - 2);
        },
        connArr.reduce((r, d) => (r += d.__data__.stroke_width), 0)
      ); // r starts with the full some of the width and then reduces
    } else {
      connOffset = connArr.reduce((r, d, i, a) => {
        if (i >= idx) return r;
        return (r += d.__data__.stroke_width - 2);
      }, 0); // r starts at zero and is added to
    }

    destinationOffset[0] = destinationOffset[0] + destinationWidth / 2;

    sourceOffset[0] = sourceOffset[0] + conn.dx;
    sourceOffset[1] =
      sourceOffset[1] +
      conn.dy +
      disp.connectorLabel.height +
      disp.connectorLabel.padding.y;

    //the parent offset is calculated by the order of the container

    let start = {
      x: disp.connectorLabel.width / 2,
      y: disp.connectorLabel.height + disp.connectorLabel.padding.y,
    };

    //calculate destination and control of bezier curve
    let end = {
      x:
        destinationOffset[0] -
        sourceOffset[0] +
        (goingRight ? connOffset * -1 : connOffset),
      y:
        destinationOffset[1] -
        sourceOffset[1] +
        (disp.connectorLabel.height + disp.connectorLabel.padding.y),
    };

    let controlPoint1 = {
      x: start.x,
      y: start.y + (end.y - start.y) / 2 + connOffset,
    };

    let controlPoint2 = {
      x: end.x,
      y: controlPoint1.y,
    };

    // let connControlPointRadius = connArr.reduce((r,d,i,a) => {
    //     if (i >= idx) return r;
    //     return r +=  d.__data__.stroke_width;
    // },
    //     Math.abs(controlPoint1.x - controlPoint2.x) < disp.connectorLine.maxArcRadius ?
    //         Math.abs(controlPoint1.x - controlPoint2.x) :
    //         disp.connectorLine.maxArcRadius
    // );

    //get arc Radii
    controlPoint1.arcRadius =
      Math.abs(controlPoint1.x - controlPoint2.x) <
      disp.connectorLine.maxArcRadius
        ? Math.abs(controlPoint1.x - controlPoint2.x)
        : disp.connectorLine.maxArcRadius;

    controlPoint2.arcRadius =
      Math.abs(controlPoint2.y - end.y) < disp.connectorLine.maxArcRadius
        ? Math.abs(controlPoint2.y - end.y)
        : disp.connectorLine.maxArcRadius;

    //path doesn't return context, so you can't chain methods.
    let lineGen = d3.path();
    lineGen.moveTo(start.x, start.y);
    lineGen.lineTo(
      controlPoint1.x,
      controlPoint1.y - disp.connectorLine.maxArcRadius
    );
    lineGen.arcTo(
      controlPoint1.x,
      controlPoint1.y,
      controlPoint2.x,
      controlPoint2.y,
      controlPoint1.arcRadius
    );
    lineGen.arcTo(
      controlPoint2.x,
      controlPoint2.y,
      end.x,
      end.y,
      controlPoint2.arcRadius
    );
    lineGen.lineTo(end.x, end.y);

    return lineGen;
  };

  getTranslationOfGroup = (d) => {
    let { DISPLAY_SETTINGS: disp, startBoxSavedOffset } = this;

    let x = d.pos_left - startBoxSavedOffset.x + disp.viewBox.defaultPadding.x;
    let y = d.pos_top - startBoxSavedOffset.y + disp.viewBox.defaultPadding.y;

    return { x, y };
  };

  selectElementGroupData = (elem) => {
    return { ...elem, width: this.getBoxWidth(elem) };
  };

  getTitleHTML = (elem) => {
    return `<p>${elem.display_name}</p>`;
  };

  buildScriptBox = (elem) => {
    return (
      <div
        className={`script-box ${
          elem.element_hash ===
          _get(this.props, "editingElement.element_hash", "xx")
            ? "editing"
            : ""
        }`}
        style={{ height: "100%", width: "100%" }}
      >
        <h6 className={"primary-text script-box__title"}>
          {this.displayOrTruncateTextWithEllipsis(elem.display_name, 60)}
        </h6>
        <p className={"script-box__prompt"}>
          {this.displayOrTruncateTextWithEllipsis(elem.prompt, 100)}
        </p>
      </div>
    );
  };

  buildScriptElementIcon = (elem) => {
    return (
      <div className={"script-box-icon is-flex"}>
        <i className={elem.iconClass} />
      </div>
    );
  };

  renderConnLabel = (con) => {
    if (this.props.is_editable) {
      return (
        <div className={`script-con `}>
          <span className={"script-con__name"}>
            {this.displayOrTruncateTextWithEllipsis(
              con.value_text,
              this.DISPLAY_SETTINGS.connectorLabel.maxTextLength
            )}
          </span>
          <span
            className={`script-con__connect_button ${
              this.state.linkSource.map((d) => d.key).includes(con.key)
                ? "selected-conn-source"
                : ""
            }`}
            id={"connect-button-" + con.key}
          />
        </div>
      );
    } else {
      return (
        <div className={"script-con"}>
          <span className={"script-con__name"}>
            {this.displayOrTruncateTextWithEllipsis(
              con.value_text,
              this.DISPLAY_SETTINGS.connectorLabel.maxTextLength
            )}
          </span>
          <span className={"script-con__count"}>{con.response_count}</span>
        </div>
      );
    }
  };

  setConnectorValueText = (elem, conn) => {
    //questions that have multiple choice answers and values already set
    if (conn.value_text)
      return this.displayOrTruncateTextWithEllipsis(
        conn.value_text,
        this.DISPLAY_SETTINGS.connectorLabel.maxTextLength
      );

    //if we're editing and it's not an anser, return nothing
    if (this.props.is_editable) {
      return null;
    }

    //questions that you can't answer
    if (["start"].includes(elem.elementtype)) {
      return "Pass Through";
    }

    //questions you can answer, but are open responses
    else {
      return "Responses";
    }
  };

  //map everything you need to build connections
  selectConnectorData = (elem, idx, elemSelectionArray) => {
    let { DISPLAY_SETTINGS: disp } = this;

    let connectors = elem.connectors.map((c, i, a) => ({
      ...c,
      value_text: this.setConnectorValueText(elem, c),
      response_count: this.props.is_editable
        ? c.response_count || c.response_count === 0
          ? c.response_count
          : elem.response_count
        : null,
      dx: this.props.is_editable
        ? disp.connectorLabel.width * i +
          (this.getBoxWidth(elem) - disp.connectorLabel.width * a.length) / 2
        : 4,
      dy: disp.box.height + 10,
      parentData: elem,
    }));

    /*
     * now we need to add a connector for the "pass through" responses, which apply to everything that isn't
     * a multiple choice question and you can provide an answer to. Basically, we want to know who answered and who didn't.
     *
     * The first step is finding out the total number of responses that branch to the elem that we are talking about.
     * */
    if (
      !["start", "question", "end"].includes(elem.elementtype) &&
      elem.connectors.length
    ) {
      let prevLevel = elem.level - 1;
      let prevLevelElems = this.state.data.filter((e) => e.level === prevLevel);
      let connectingResponses = 0;
      let passThroughs = 0;

      prevLevelElems.forEach((e) => {
        e.connectors.forEach((c, i, a) => {
          //the element is pointing towards us
          if (c.next_el_hash === elem.element_hash) {
            connectingResponses += this.getConnResponseCountForInterpolation(
              { ...c, parentData: e },
              i,
              a
            );
          }
        });
      });

      //to compute pass throughs, we need to know the maximum number of responses downstream, so we find the next element that is a question.
      // to find this we have to crawl downstream from the connectors.

      const getNextQuestion = (next_el_hash) => {
        let nextElem = this.state.data.find(
          (_d) => _d.element_hash === next_el_hash
        );
        if (!nextElem) return;
        if (nextElem.elementtype === "end" || !nextElem.connectors.length) {
          return null;
        } else if (nextElem.elementtype === "question") {
          return nextElem;
        } else {
          getNextQuestion(nextElem.connectors[0].next_el_hash);
        }
      };

      //only one connector for this kind of element
      let nextQuestion = getNextQuestion(connectors[0].next_el_hash);

      if (nextQuestion) {
        let nextQuestionRespCount = nextQuestion.response_count;
        let passThroughsPossible = nextQuestionRespCount - elem.response_count;
        if (passThroughsPossible > connectingResponses) {
          passThroughs = connectingResponses - elem.response_count;
        } else {
          passThroughs = passThroughsPossible - elem.response_count;
        }
      } else {
        passThroughs = connectingResponses - elem.response_count;
      }

      if (passThroughs > 0) {
        // if (elem.elementtype == 'speech') connectors = []; //to avoid the annoying zero response that would necessarily trail if it were not removed
        connectors.push({
          value_text: "Pass Through",
          response_count: passThroughs,
          dx:
            disp.connectorLabel.width * connectors.length +
            (this.getBoxWidth(elem) -
              disp.connectorLabel.width * connectors.length) /
              2,
          dy: disp.box.height + 10,
          parentData: elem,
          next_el_hash: connectors[0].next_el_hash,
        });
        if (elem.elementtype === "speech") {
          // connectors = connectors.filter(d => d.value_text === 'responses')
        }
      }
    }

    connectors = connectors
      .map((c, i, a) => ({
        ...c,
        response_count: this.getConnResponseCountForInterpolation(c, i, a),
      }))
      .map((c, i, a) => ({
        ...c,
        stroke_width: Math.round(this.getConnStrokeWidth(c)),
      }));
    // .filter(c => c.response_count !== 0 && ['speech', 'start'].includes(elem.elementtype))
    return connectors;
  };

  getConnectorKey = (conn) => {
    return `${conn.value_text || "single"}-${conn.parentData.element_hash}`;
  };

  //mapping of data here prevents recalculation of dimensions and allows us to store them on the
  // element so they can be referenced later for connections

  //0 response count elements need special handling since many are speech bubbles that will never have responses.
  //interpolate instead of zero the total number of responses that went through the speech
  getConnResponseCountForInterpolation = (d, i, a) => {
    let { response_count } = d;

    //speech questions have zero response count, so handle that case.
    if (["start"].includes(d.parentData.elementtype)) {
      //use the max response count to simulate that everyone got the speech
      response_count = this.maxResponseCount;
    }

    return response_count;
  };

  getConnStrokeWidth = (conn) => {
    if (this.props.is_editable) return 2;
    return this.connectorWidthScale(conn.response_count);
  };

  getConnColor = (d, i, a) => {
    if (this.props.is_editable) {
      return d.color || "#a4a4a4";
    } else {
      if (d.next_el_display_name === "END" || d.response_count === 0)
        return "#d4d4d4";
      if (d.value_text === "Pass Through") return "#9b4dca";
      return "#1679c7";
    }
  };

  getConnOpacity = (d, i, a) => {
    if (d.next_el_display_name === "END") {
      return ".4";
    } else {
      return "1";
    }
  };

  // connBoxClickAction = function(_t = this) {
  //     let t = d3.select(this); //how do i store where this is?
  //     _t.setState({linkSource: _get(t.data(), '[0]', [])});
  //     _t.svg.selectAll('.script-con').classed('disabled', true)
  //     console.log(d3.select(t.parentNode.parentNode.parentNode))
  // }
  getConnBoxClickAction = () => {
    const _t = this;
    return function () {
      let t = d3.select(this);
      //get element data, which also has the connection's parent data
      let t_data = _get(t.data(), "[0]", []);
      //
      _t.svg.selectAll(".script-con").classed("disabled", true);

      if (t_data.parentData?.elementtype === "multiselect") {
        let scriptElemGroupSelector =
          "#g-" + _get(t_data, "parentData.element_hash", "SELECT_FAILURE");
        let element_data = d3.select(scriptElemGroupSelector).data()[0];
        _t.setState({
          linkSource: [
            ..._t.state.linkSource,
            ...element_data.connectors.map((d) => ({
              ...d,
              parentData: element_data,
            })),
          ],
        });
        // let element_data = _t.svg.selectAll("g.script-group").data().find(ud => ud.element_hash === t.data()[0].element_hash)
        // _t.setState({linkSource: [..._t.state.linkSource, ...element_data.connectors.map(d => ({...d, parentData: element_data}))]})
        //this is the same thing as below, but we need to select ALL of the boxes on the multiselect box, regardless of which one you click
        // make the script elements in the group not disabled.
        // this way we can select multiple elements to point to a single target if we want to.
        d3.select(scriptElemGroupSelector)
          .selectAll(".script-con")
          .classed("disabled", false);
        d3.select(scriptElemGroupSelector)
          .selectAll(".script-con__connect_button")
          .classed("selected-conn-source", true);
      } else {
        _t.setState({ linkSource: [..._t.state.linkSource, t_data] }, () => {
          _t.state.linkSource.forEach((d, i) => {
            _t.svg
              .selectAll("#connect-button-" + d.key)
              .classed("selected-conn-source", true);
          });
        });
        //now we get the selector of the top level group from the element data
        let scriptElemGroupSelector =
          "#g-" + _get(t_data, "parentData.element_hash", "SELECT_FAILURE");
        // make the script elements in the group not disabled.
        // this way we can select multiple elements to point to a single target if we want to.
        d3.select(scriptElemGroupSelector)
          .selectAll(".script-con")
          .classed("disabled", false);
      }

      // t.select('.script-con__connect_button').classed('selected-conn-source', true)
      //now we get the selector of the top level group from the element data
      let scriptElemGroupSelector =
        "#g-" + _get(t_data, "parentData.element_hash", "SELECT_FAILURE");
      // make the script elements in the group not disabled.
      // this way we can select multiple elements to point to a single target if we want to.
      d3.select(scriptElemGroupSelector)
        .selectAll(".script-con")
        .classed("disabled", false);
    };
  };

  clearBuildConnectionMode = () => {
    this.setState({ linkSource: [] });
    this.svg.selectAll(".script-con").classed("disabled", false);
    this.svg
      .selectAll(".selected-conn-source")
      .classed("selected-conn-source", false);
  };

  editConnectionStateData = (connectionKey, dataToInsert = {}) => {
    let { data: newData } = this.state;
    //update the data in the connectors

    newData.forEach((d) => {
      d.connectors.forEach((conn) => {
        if (conn.key === connectionKey) {
          Object.keys(dataToInsert).forEach((k) => {
            conn[k] = dataToInsert[k];
          });
        }
      });
    });

    this.setState({ newData });
  };

  handleConnectionAssignment = (target_elem_hash) => {
    //need to prevent recursion, so start by finding the element and make sure that we don't have infinite loop.
    const target_element = this.state.data.find(
      (d) => d.element_hash === target_elem_hash
    );
    if (!target_element || !this.state.linkSource.length) return;
    const source_element_hash = _get(
      this.state,
      "linkSource[0].parentData.element_hash",
      "--"
    );

    //you tried to connect it to the start element, not allowed, return. Same if you tried to connect it to itself
    if (
      target_elem_hash === "start" ||
      target_elem_hash === source_element_hash
    )
      return;

    //if there's a loop, return
    let link_creates_loop = false;

    const checkForLoopInElement = (targ_elem) => {
      if (link_creates_loop) return;
      targ_elem.connectors.forEach((d) => {
        if (d.next_el_hash === source_element_hash) {
          link_creates_loop = true;
        } else {
          let next_target_element = this.state.data.find(
            (elem) => elem.element_hash === d.next_el_hash
          );
          if (next_target_element) checkForLoopInElement(next_target_element);
        }
      });
    };

    checkForLoopInElement(target_element);

    if (link_creates_loop) return;

    this.state.linkSource.forEach((conn) => {
      //check if we create an infinite loop in the script

      this.editConnectionStateData(conn.key, {
        next_el_hash: target_elem_hash,
      });
    });
    this.clearBuildConnectionMode();
  };

  handleXYUpdate = (element_hash, x, y) => {
    let { data } = this.state;

    data.forEach((d) => {
      if (d.element_hash === element_hash) {
        d.pos_left = x;
        d.pos_top = y;
      }
    });
    this.setState({ data });
  };

  getDragHandler = () => {
    let _t = this;
    let disp = _t.DISPLAY_SETTINGS;
    return d3
      .drag()
      .on("drag", function (ev) {
        const t = d3.select(this);
        const elemdata = t.data()[0];
        // console.log(t, t.node().parentNode)
        //

        // console.log(elemdata.pos_left, ev.x) //, t.data()[0].pos_top, ev.y - disp.box.height/2)
        const newX = ev.x - _t.getBoxWidth(t.data()[0]) / 2;
        const newY = ev.y - disp.box.height / 2;

        if (!_t.dragArray[0]) {
          _t.dragArray = [newX, newY];
          return null;
        } else if (
          Math.abs(_t.dragArray[0] - newX) < 10 ||
          Math.abs(_t.dragArray[1] - newY) < 10
        ) {
          return;
        }

        t.attr("x", ev.x).attr("transform", `translate(${newX}, ${newY})`);
        _t.buildConnections();
      })
      .on("end", function (ev) {
        const elemdata = d3.select(this).data()[0];
        const t = _t.svg.select("#g-" + elemdata.element_hash);
        const newX = ev.x - _t.getBoxWidth(t.data()[0]) / 2;
        const newY = ev.y - disp.box.height / 2;
        _t.dragArray = [null, null];
        _t.handleXYUpdate(t.data()[0].element_hash, newX, newY);
      });
  };

  buildSVGWrapper = () => {
    let { DISPLAY_SETTINGS: disp } = this;

    this.svg = d3
      .select(this.d3Target)
      .append("svg")
      .classed("scriptResponses", true);
    // .attr('viewBox', `${disp.viewBox.x} ${disp.viewBox.y} ${disp.viewBox.x} ${disp.viewBox.y}`)

    this.svg.on("dblclick", () => {
      if (this.state.linkSource.length) {
        this.clearBuildConnectionMode();
      }
    });
  };

  buildConnections = () => {
    //apply the foreign object itself
    let _enter = (selection) => {
      //bind the actual groups to the selection
      selection.attr("transform", (d) => `translate(${d.dx}, ${d.dy})`);

      let connLabels = selection
        .append("foreignObject")
        .attr("height", this.DISPLAY_SETTINGS.connectorLabel.height)
        .attr("width", this.DISPLAY_SETTINGS.connectorLabel.width)
        .classed("script-conn-label", true)
        .html((d) => renderToString(this.renderConnLabel(d)));

      if (this.props.is_editable) {
        connLabels.on("click", this.getConnBoxClickAction());
      }

      //append lines
      selection
        .append("path")
        .attr("d", (d, i, a) => this.getConnectorLine(d, i, a))
        .attr("stroke-width", (d) => d.stroke_width)
        .attr("stroke", this.getConnColor)
        .attr("opacity", this.getConnOpacity)
        .attr("fill", "transparent");
    };

    let _update = (selection) => {
      let updatedData = selection.data();

      selection.attr("transform", (d) => {
        let _d = updatedData.find((ud) => ud.key === d.key);
        return `translate(${_d.dx}, ${_d.dy})`;
      });

      selection.selectAll("path").attr("d", (d, i, a) => {
        //this is sort of a hack, i'm updating the new next el hash manually, because seems to not be working
        let _d = updatedData.find((ud) => ud.key === d.key);
        d.next_el_hash = _d.next_el_hash;
        return this.getConnectorLine(_d, i, [...a]);
      });

      selection.selectAll(".script-conn-label").html((d) => {
        let nd = updatedData.find((ud) => ud.key === d.key);
        return renderToString(this.renderConnLabel(nd || {}));
      });
    };

    let _exit = (selection) => {
      selection.remove();
    };

    //build groups, bind connection data, apply the selection functions
    this.conns = this.svg
      .selectAll("g.script-group")
      .selectAll("g.connectors")
      .selectAll("g.connector")
      .data(
        (d, i, a) =>
          this.selectConnectorData(
            this.state.data.find(
              (elem) => elem.element_hash === d.element_hash
            ),
            i,
            this.state.data
          ),
        (conn) => conn.key
      )
      .join(
        (enter) => _enter(enter.append("g").classed("connector", true)),
        (update) => _update(update),
        (exit) => _exit(exit)
      );
  };

  buildScript = () => {
    let { DISPLAY_SETTINGS: disp } = this;
    let _t = this;
    let data = this.state.data;

    const _enter = (selection) => {
      let elems = selection
        .append("g")
        .classed("script-group", true)
        .attr("id", (d) => this.getElemSelector(d.element_hash))
        .attr("box-width", (d) => d.width) // so we can get it later in building connectors
        .attr("transform", (d) => {
          let translation = this.getTranslationOfGroup(d);
          return `translate(${translation.x}, ${translation.y})`;
        });

      elems.append("g").attr("class", "connectors");

      let rects = elems.append("g").classed("script-rect-group", true);

      rects
        .append("rect")
        .classed("shadow-box", true)
        .attr("fill", "transparent")
        .attr("stroke", "black")
        .attr("stroke-width", 4)
        .attr("stroke-opacity", 0.1)
        .attr("height", disp.box.height)
        .attr("width", (d) => d.width)
        .attr("rx", 4)
        .style("filter", "url(#drop-shadow)");

      let rectHTMLInnerElements = elems
        .append("foreignObject")
        .attr("height", disp.box.height)
        .attr("width", (d) => d.width)
        .classed("element-box-wrapper", true)
        .html((d) => renderToString(this.buildScriptBox(d)));

      let rectIcons = elems
        .append("foreignObject")
        .attr("height", disp.box.icon.height)
        .attr("width", disp.box.icon.width)
        .attr(
          "transform",
          `translate(${disp.box.icon.dx}, ${disp.box.icon.dy})`
        )
        .html((d) => renderToString(this.buildScriptElementIcon(d)));

      //event handlers
      this.getDragHandler()(elems);

      rectHTMLInnerElements.on("mouseover", function (d) {
        // if (!this.connectionMode) return;
        d3.select(this).selectAll(".script-box").classed("hovered", true);
      });

      rectHTMLInnerElements.on("mouseout", function (d) {
        // if (!this.connectionMode) return;
        d3.select(this).selectAll(".script-box").classed("hovered", false);
      });

      rectHTMLInnerElements.on("click", function () {
        if (_t.state.linkSource.length) {
          let t = d3.select(this);
          _t.handleConnectionAssignment(_get(t.data(), "[0].element_hash"));
        }
      });

      rectHTMLInnerElements.on("dblclick", function () {
        if (_t.props.is_editable && _t.props.onEditElementRequest) {
          let t = d3.select(this);
          let element_data = _t.svg
            .selectAll("g.script-group")
            .data()
            .find((ud) => ud.element_hash === t.data()[0].element_hash);
          if (element_data.elementtype === "start") return;
          _t.props.onEditElementRequest(element_data);
        }
      });
    };

    const _update = (selection) => {
      let updatedData = selection.data();

      const getUpdatedElem = (element_hash) =>
        updatedData.find((ud) => ud.element_hash === element_hash) || {};

      selection
        .selectAll(".connectors")
        .data((d) =>
          updatedData.find((ud) => ud.element_hash === d.element_hash)
        );

      selection.attr("box-width", (d) =>
        this.getBoxWidth(getUpdatedElem(d.element_hash))
      ); // so we can get it later in building connectors

      selection
        .selectAll(".element-box-wrapper")
        .attr("width", (d) => this.getBoxWidth(getUpdatedElem(d.element_hash)))
        .html((d) =>
          renderToString(this.buildScriptBox(getUpdatedElem(d.element_hash)))
        );

      selection
        .selectAll("rect")
        .attr("width", (d) => this.getBoxWidth(getUpdatedElem(d.element_hash)));
    };

    const _exit = (selection) => {
      selection.remove();
    };

    this.svg
      .selectAll("g.script-group")
      .data(data.map(this.selectElementGroupData), (d) => "" + d.element_hash)
      .join(
        (enter) => _enter(enter),
        (update) => _update(update),
        (exit) => _exit(exit)
      );

    this.buildConnections();
    //apply drag handling to top level groups.
  };

  appendDefs = () => {
    // filters go in defs element
    const defs = this.svg.append("defs");

    // create filter with id #drop-shadow
    // height=130% so that the shadow is not clipped
    const filter = defs.append("filter").attr("id", "drop-shadow");
    // .attr("height", "98%")
    // .attr('width', "98%")

    filter
      .append("feDropShadow")
      .attr("stdDeviation", 4)
      .attr("flood-color", "black");
  };

  render() {
    return (
      <>
        {/*{(!this.state.data || !this.state.data.length || !this.state.valid) &&*/}
        {/*    <p>We can't display this script.</p>*/}
        {/*}*/}
        <div ref={(node) => (this.d3Target = node)} />
      </>
    );
  }
}
